import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, FileText, MessageCircle } from "lucide-react";
import SignaturePad from "../components/SignaturePad";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatIDR(n) {
  return `Rp ${Number(n || 0).toLocaleString("id-ID")}`;
}

export default function PublicSign() {
  const { kind, token } = useParams(); // 'memo' | 'sale'
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [signerName, setSignerName] = useState("");
  const [signerPhone, setSignerPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [signed, setSigned] = useState(false);
  const [pdfUrl, setPdfUrl] = useState("");
  const [stampPending, setStampPending] = useState(false);
  const padRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/public/sign/${kind}/${token}`);
        if (cancelled) return;
        setDoc(data);
        if (data.signed) {
          setSigned(true);
          setPdfUrl(`${API_BASE}/public/sign/${kind}/${token}/pdf`);
          setSignerName(data.signer_name || "");
        }
      } catch (e) {
        if (cancelled) return;
        setErr(e.response?.data?.detail || "This signing link is invalid or expired.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [kind, token]);

  // Defensive: under StrictMode double-invoke or stale renders, doc can briefly
  // be null while loading=false. Show the spinner until both conditions clear.
  if (loading || (!doc && !err)) {
    return (
      <FullPage>
        <Loader2 className="w-6 h-6 animate-spin text-[#D4AF37]" />
        <div className="overline mt-3 text-[#8A8A8A]">Loading document…</div>
      </FullPage>
    );
  }

  if (err || !doc) {
    return (
      <FullPage>
        <div className="serif text-3xl text-[#C99356] mb-2">Link unavailable</div>
        <div className="text-sm text-[#8A8A8A] max-w-md text-center">{err || "Document could not be loaded."}</div>
      </FullPage>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (!signerName.trim()) return setErr("Please type your full name.");
    if (padRef.current.isEmpty()) return setErr("Please draw your signature.");
    setErr("");
    setSubmitting(true);
    try {
      const signature_png = padRef.current.toDataURL();
      const { data } = await axios.post(`${API_BASE}/public/sign/${kind}/${token}`, {
        signature_png,
        signer_name: signerName.trim(),
        signer_phone: signerPhone.trim(),
      });
      setSigned(true);
      setPdfUrl(`${API_BASE}${data.pdf_url}`);
      if (data.requires_emeterai) setStampPending(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Could not submit signature.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#EAEAEA] flex flex-col">
      {/* Top gold band */}
      <div className="h-1 bg-[#D4AF37]" />
      <header className="px-5 lg:px-10 py-6 border-b border-white/5 flex items-center justify-between gap-4">
        <div className="flex flex-col gap-2">
          <img
            src="/brand/opikjewels-logo-v2.png"
            alt="Opik Jewels"
            className="h-[70px] w-auto object-contain"
            data-testid="public-brand-logo"
          />
          <div>
            <div className="overline text-[#D4AF37]">Premium Atelier</div>
            <h1 className="serif text-2xl lg:text-3xl mt-1">
              {doc.kind === "memo" ? "Stone-Out Memo" : "Invoice Acceptance"}{" "}
              <span className="mono text-base text-[#8A8A8A] ml-2">{doc.number}</span>
            </h1>
          </div>
        </div>
        {pdfUrl && (
          <a
            href={pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="download-signed-pdf"
            className="btn-outline-gold px-4 py-2.5 inline-flex items-center gap-2 text-xs uppercase tracking-wider"
          >
            <FileText className="w-3.5 h-3.5" /> Download Signed PDF
          </a>
        )}
      </header>

      <main className="flex-1 max-w-3xl w-full mx-auto px-5 lg:px-10 py-8 lg:py-12 space-y-8">
        {signed && (
          <div
            className="border border-[#7BA585]/40 bg-[#7BA585]/[0.06] p-6 flex items-start gap-4"
            data-testid="signed-banner"
          >
            <CheckCircle2 className="w-6 h-6 text-[#7BA585] shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="serif text-xl text-[#7BA585]">
                Document signed{signerName ? ` by ${signerName}` : ""}.
              </div>
              <div className="text-sm text-[#8A8A8A] mt-1">
                A timestamped, signed copy has been generated. Please download it below
                or save the link to this page.
              </div>
              {stampPending && (
                <div className="mt-3 border border-[#C99356]/30 bg-[#C99356]/[0.05] px-3 py-2 text-[0.75rem] text-[#C99356]" data-testid="emeterai-pending-notice">
                  This document requires <strong>e-Meterai (Rp 10.000)</strong>. Our team will apply
                  the digital stamp shortly and resend you the official PDF.
                </div>
              )}
              <div className="flex flex-wrap gap-2 mt-4">
                <a
                  href={pdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-gold px-4 py-2 inline-flex items-center gap-2 text-xs uppercase tracking-wider"
                  data-testid="download-pdf-banner"
                >
                  <FileText className="w-3.5 h-3.5" /> Open Signed PDF
                </a>
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(
                    `${doc.kind === "memo" ? "Memo" : "Invoice"} ${doc.number} signed — copy: ${pdfUrl}`,
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-outline-gold px-4 py-2 inline-flex items-center gap-2 text-xs uppercase tracking-wider"
                  data-testid="share-whatsapp"
                >
                  <MessageCircle className="w-3.5 h-3.5" /> Share via WhatsApp
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Document preview */}
        <section className="border border-[#D4AF37]/20 bg-[#0F0F0F] p-6 lg:p-8 space-y-5" data-testid="doc-preview">
          <div className="grid grid-cols-2 gap-6 text-sm">
            <div>
              <div className="overline mb-1">Consigned to / Bill to</div>
              <div className="serif text-lg">{doc.customer_name}</div>
            </div>
            {doc.kind === "memo" ? (
              <div>
                <div className="overline mb-1">Due date</div>
                <div className="mono text-lg">{doc.due_date}</div>
              </div>
            ) : (
              <div>
                <div className="overline mb-1">Payment status</div>
                <div className="mono text-lg text-[#D4AF37]">{doc.payment_status}</div>
              </div>
            )}
          </div>

          <div className="border-t border-white/5 pt-4">
            <div className="overline mb-3">Pieces</div>
            <div className="space-y-2">
              {(doc.items || []).map((it, i) => (
                <div
                  key={it.line_id || it.product_id || `item-${i}`}
                  className="grid grid-cols-12 gap-3 items-center py-2 border-b border-white/5 last:border-0 text-sm"
                  data-testid={`doc-item-${i}`}
                >
                  <div className="col-span-6">
                    <div>{it.product_name}</div>
                    {it.variant_label && (
                      <div className="text-[0.7rem] mono text-[#7a7a7a]">{it.variant_label}</div>
                    )}
                  </div>
                  <div className="col-span-2 mono text-[#8A8A8A] text-[0.75rem]">{it.product_sku}</div>
                  <div className="col-span-1 mono text-right">{it.quantity}</div>
                  <div className="col-span-3 mono text-right text-[#D4AF37]">
                    {formatIDR(it.memo_price ?? it.unit_price ?? it.line_subtotal)}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="border-t border-[#D4AF37]/30 pt-4 flex justify-between items-baseline">
            <span className="overline text-[#D4AF37]">
              {doc.kind === "memo" ? "Total Value" : "Total Due"}
            </span>
            <span className="serif text-2xl text-[#D4AF37]" data-testid="doc-total">
              {formatIDR(doc.total ?? doc.total_value)}
            </span>
          </div>

          {doc.kind === "sale" && doc.payment_status !== "Paid" && (
            <div className="border-t border-white/5 pt-3 grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-[#8A8A8A]">Paid to date</span>
                <div className="mono text-[#7BA585] text-base mt-0.5">{formatIDR(doc.paid_total)}</div>
              </div>
              <div className="text-right">
                <span className="text-[#8A8A8A]">Balance due</span>
                <div className="mono text-[#C99356] text-base mt-0.5">{formatIDR(doc.balance_due)}</div>
              </div>
            </div>
          )}
        </section>

        {/* Signing section */}
        {!signed && (
          <form onSubmit={submit} className="border border-[#D4AF37]/30 bg-[#0F0F0F] p-6 lg:p-8 space-y-5" data-testid="sign-form">
            <div>
              <div className="overline text-[#D4AF37]">Step 1</div>
              <h2 className="serif text-2xl mt-1">Confirm your details</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="overline mb-2">Full name (as on ID) *</div>
                <input
                  required
                  value={signerName}
                  onChange={(e) => setSignerName(e.target.value)}
                  className="luxe-input w-full px-4 py-2.5 text-sm"
                  data-testid="signer-name"
                  placeholder="e.g. John Doe"
                />
              </div>
              <div>
                <div className="overline mb-2">Mobile (optional)</div>
                <input
                  type="tel"
                  value={signerPhone}
                  onChange={(e) => setSignerPhone(e.target.value)}
                  className="luxe-input w-full px-4 py-2.5 text-sm mono"
                  data-testid="signer-phone"
                  placeholder="+62 ..."
                />
              </div>
            </div>

            <div>
              <div className="overline text-[#D4AF37]">Step 2</div>
              <h2 className="serif text-2xl mt-1 mb-3">Sign below</h2>
              <SignaturePad ref={padRef} width={620} height={220} testid="signature-pad" />
            </div>

            {err && <div className="text-sm text-red-400" data-testid="sign-error">{err}</div>}

            <div className="border-t border-white/5 pt-5 text-xs text-[#7a7a7a] leading-relaxed">
              By submitting, you acknowledge you have read this {doc.kind === "memo" ? "memo" : "invoice"} and
              consent to capture of your signature, IP address, and device information for legal record.
              The document will be locked from further edits after signing.
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="btn-gold w-full py-3.5 text-sm uppercase tracking-wider"
              data-testid="submit-sign"
            >
              {submitting ? "Submitting…" : "I Confirm & Sign"}
            </button>
          </form>
        )}
      </main>

      <footer className="px-5 lg:px-10 py-5 border-t border-white/5 text-center text-xs text-[#666] mono">
        OPIK JEWELS  ·  CONFIDENTIAL  ·  SIGNED COPY ARCHIVED
      </footer>
      <div className="h-1 bg-[#D4AF37]" />
    </div>
  );
}

function FullPage({ children }) {
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#EAEAEA] flex flex-col items-center justify-center px-5">
      {children}
    </div>
  );
}
